import React, { useMemo, useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import Logo from "../assets/logo-container.png";
import { Colors } from "../Helper/themes";
import "../Styles/LeafWithLogo.css";
import Swal2 from "sweetalert2";
import axios from "axios";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import success from "./../assets/success-green.png";
import tasty from "./../assets/tasty.png";
import checkCircle from "./../assets/check_circle.png";
import errorCircle from "./../assets/myplancancel.png";

const LOGO_BASE_W = 768;
const LOGO_BASE_H = 475;
const LOGO_PADDING = 32;

export default function LeafWithLogo() {
  const [screenW, setScreenW] = useState(window.innerWidth);
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [loader, setLoader] = useState(false);

  // State for text carousel animation
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Text items for the animation
  const textItems = [
    "Fresh Menu Everyday",
    "No Subscription Stress",
    "Gut friendly, and yummy",
    "Super Affordable",
  ];

  useEffect(() => {
    const handleResize = () => setScreenW(Math.min(window.innerWidth, 400));
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fix mobile viewport height issues with address bar
  useEffect(() => {
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    // Set initial viewport height
    setViewportHeight();

    // Update on resize and orientation change
    window.addEventListener("resize", setViewportHeight);
    window.addEventListener("orientationchange", setViewportHeight);

    // Prevent scrolling on this page
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      // Cleanup: restore scrolling when leaving this component
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      window.removeEventListener("resize", setViewportHeight);
      window.removeEventListener("orientationchange", setViewportHeight);
    };
  }, []);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "null");

    if (user) {
      setTimeout(() => {
        navigate("/home", { replace: true });
      }, 200);
    }
  }, []);

  const { referralCode } = useParams();

  const location = useLocation(); // Add this

  // Determine acquisition channel based on the route
  const getAcquisitionChannel = () => {
    if (location.pathname.includes("/apt/alpine")) {
      return "alpine";
    } else if (location.pathname.includes("/apt/hiranandani")) {
      return "hiranandani";
    } else if (location.pathname.includes("/flyer")) {
      return "flyer";
    } else if (location.pathname.includes("/standie")) {
      return "standie";
    } else if (location.pathname.includes("/stall")) {
      return "stall";
    } else if (location.pathname.includes("/lift-banner")) {
      return "lift_banner";
    } else if (location.pathname.includes("/alpine")) {
      return "alpine";
    } else if (location.pathname.includes("/door-tag")) {
      return "door_tag";
    } else return null; // Let backend handle default
  };

  // Fire-and-forget scan/visit tracking — counts every page load on a marketing
  // channel route, independent of whether the visitor is already logged in
  // (registration attribution alone would undercount repeat scans).
  // hasTrackedVisit guards against React.StrictMode's dev-only double-invoke
  // of effects (index.js wraps <App /> in <React.StrictMode>), which would
  // otherwise fire this POST twice per real page load.
  const hasTrackedVisit = useRef(false);
  useEffect(() => {
    if (hasTrackedVisit.current) return;
    const channel = getAcquisitionChannel();
    if (!channel) return;
    hasTrackedVisit.current = true;

    // Persist on arrival, not at OTP-send: a visitor who lands here and only
    // registers later (or from another screen) must still be attributed to the
    // channel whose visit we just counted. Cleared once registration lands.
    localStorage.setItem("acquisitionChannel", channel);

    axios
      .post(
        "/User/track-visit",
        { channel },
        { baseURL: "https://dailydish.in/api" },
      )
      .catch((err) => console.log("track-visit failed", err.message));
  }, []);

  useEffect(() => {
    if (referralCode) {
      localStorage.setItem("referralCode", referralCode);
      console.log("Referral code captured:", referralCode);
    }
  }, []);

  // Custom bezier curve for animation - smart animate equivalent
  const customBezier = [0.4, 0, 0.2, 1]; // Standard easing curve

  // Handle text change with animation delay
  useEffect(() => {
    let timeoutId;
    let intervalId;

    const startAnimationCycle = () => {
      intervalId = setInterval(() => {
        // Start animation
        setIsAnimating(true);

        // Change text after 150ms delay (as per Figma)
        timeoutId = setTimeout(() => {
          setCurrentTextIndex((prevIndex) =>
            prevIndex === textItems.length - 1 ? 0 : prevIndex + 1,
          );

          // Reset animation state after the full animation duration
          setTimeout(() => {
            setIsAnimating(false);
          }, 800);
        }, 0);
      }, 2000); // Total cycle time: 4 seconds
    };

    startAnimationCycle();

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [textItems.length]);

  const headerVariants = {
    hidden: { y: -100 },
    visible: {
      y: 0,
      transition: { duration: 0.8, delay: 0.2, ease: "easeOut" },
    },
  };

  // Fixed sizing for consistent mobile appearance
  const containerWidth = 360;
  const leafWidth = Math.max(0, containerWidth - 24);
  const leafAR = useMemo(() => 1.5, []);
  const leafHeight = Math.max(0, leafWidth / leafAR);
  const logoMaxW = Math.max(0, leafWidth - 2 * LOGO_PADDING);
  const logoMaxH = Math.max(0, leafHeight - 2 * LOGO_PADDING);
  const scale = Math.min(logoMaxW / LOGO_BASE_W, logoMaxH / LOGO_BASE_H, 1);
  const LOGO_W = Math.round(LOGO_BASE_W * scale);
  const LOGO_H = Math.round(LOGO_BASE_H * scale);

  const handleContinueAsGuest = () => {
    window.location.replace("/home");
  };

  const handleOtpClick = async () => {
    if (!customerName) {
      Swal2.fire({
        toast: true,
        position: "bottom",
        icon: "error",
        title: `Please enter your full name.`,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: {
          popup: "me-small-toast",
          title: "me-small-toast-title",
        },
      });
      return;
    }
    if (!phone) {
      Swal2.fire({
        toast: true,
        position: "bottom",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        html: `
    <div class="myplans-toast-content">
      <img src="${errorCircle}" alt="Error" class="myplans-toast-check" />
      <div class="myplans-toast-text">
        <div class="myplans-toast-title">Missing Phone Number</div>
        <div class="myplans-toast-subtitle">
          Please enter your phone number.
        </div>
      </div>
    </div>
  `,
        customClass: {
          popup: "myplans-custom-toast",
          htmlContainer: "myplans-toast-html",
        },
        didOpen: () => {
          // Position above bottom nav
          const toast = document.querySelector(".myplans-custom-toast");
          if (toast) {
            toast.style.bottom = "90px";
            toast.style.left = "50%";
            toast.style.transform = "translateX(-50%)";
            toast.style.position = "fixed";
          }
        },
      });

      return;
    }
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(phone)) {
      Swal2.fire({
        toast: true,
        position: "bottom",
        icon: "error",
        title: `Please enter a valid 10-digit phone number.`,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: {
          popup: "me-small-toast",
          title: "me-small-toast-title",
        },
      });
      return;
    }
    setLoader(true);
    try {
      const config = {
        url: "/User/Sendotp",
        method: "post",
        baseURL: "https://dailydish.in/api",
        headers: { "content-type": "application/json" },
        data: {
          Mobile: phone,
          acquisitionChannel: getAcquisitionChannel(), // Add this
        },
      };

      const res = await axios(config);
      if (res.status === 401) {
        return Swal2.fire({
          toast: true,
          position: "bottom",
          icon: "error",
          title: `Invalid Phone Number`,
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: "me-small-toast",
            title: "me-small-toast-title",
          },
        });
      }
      if (res.status === 402) {
        return Swal2.fire({
          toast: true,
          position: "bottom",
          icon: "error",
          title: `Error sending OTP`,
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: "me-small-toast",
            title: "me-small-toast-title",
          },
        });
      }
      if (res.status === 200) {
        Swal2.fire({
          toast: true,
          position: "bottom",
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
          html: `
    <div class="myplans-toast-content">
      <img src="${checkCircle}" alt="Success" class="myplans-toast-check" />
      <div class="myplans-toast-text">
        <div class="myplans-toast-title">OTP Sent</div>
        <div class="myplans-toast-subtitle">
          Hi ${customerName}, OTP has been sent to your WhatsApp number
        </div>
      </div>
    </div>
  `,
          customClass: {
            popup: "myplans-custom-toast",
            htmlContainer: "myplans-toast-html",
          },
          didOpen: () => {
            // Position above bottom nav
            const toast = document.querySelector(".myplans-custom-toast");
            if (toast) {
              toast.style.bottom = "90px";
              toast.style.left = "50%";
              toast.style.transform = "translateX(-50%)";
              toast.style.position = "fixed";
            }
          },
        });

        setTimeout(() => {
          setLoader(false);
          navigate("/otp-varification", {
            state: {
              phone,
              Fname: customerName,
              acquisitionChannel: getAcquisitionChannel(),
            }, // Pass acquisition
          });
        }, 2000);
      }
    } catch (error) {
      setLoader(false);
      Swal2.fire({
        toast: true,
        position: "bottom",
        icon: "error",
        title: error.response?.data?.error || `Something went wrong!`,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: {
          popup: "me-small-toast",
          title: "me-small-toast-title",
        },
      });
      console.log("error", error.message);
    }
  };

  return (
    <div
      className="login-page-wrapper"
      style={{ height: "calc(var(--vh, 1vh) * 100)", overflow: "hidden" }}
    >
      <div className="login-container">
        <motion.div
          className="header-wrapper"
          initial="hidden"
          animate="visible"
          variants={headerVariants}
        >
          <div className="login-header">
            <div
              className="logo-container"
              style={{
                width: "100%",
                height: "100%",
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  position: "relative",
                  width: "clamp(200px, 60vw, 300px)", // Responsive width
                  height: "clamp(120px, 15vh, 180px)", // Responsive height
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "0px",
                }}
              >
                <img
                  src={"/Assets/logo-container.png"}
                  alt="Logo"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Main yellow container with animation */}
          <div
            className="yellow-container"
            style={{
              height: "clamp(35px, 5vh, 45px)", // Responsive height
              background: "#E6B800",
              border: "2px solid #F5DEB3",
              borderRadius: "0px 0px 32px 32px",
              overflow: "hidden",
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
          >
            <AnimatePresence initial={false}>
              <motion.div
                key={currentTextIndex}
                initial={{ x: "120%" }}
                animate={{
                  x: "0%",
                  transition: {
                    delay: 0,
                    x: {
                      duration: 0.7,
                      ease: customBezier,
                    },
                  },
                }}
                exit={{
                  x: "-180%",
                  transition: {
                    duration: 0.7,
                    ease: customBezier,
                  },
                }}
                style={{
                  position: "absolute",
                  whiteSpace: "nowrap",
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "clamp(14px, 3.5vw, 16px)", // Responsive font size
                  fontWeight: "500",
                  color: "#333",
                  textAlign: "center",
                }}
              >
                {textItems[currentTextIndex]}{" "}
                <motion.img
                  src={success}
                  alt=""
                  style={{
                    width: "clamp(16px, 4vw, 20px)", // Responsive icon size
                    display: "inline-block",
                  }}
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{
                    scale: 1,
                    rotate: 0,
                    transition: {
                      delay: 0.15,
                      duration: 0.9,
                      ease: customBezier,
                    },
                  }}
                  exit={{
                    scale: 0,
                    rotate: 180,
                    transition: {
                      duration: 0.9,
                      ease: customBezier,
                    },
                  }}
                />
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>

        <div className="main-content">
          <div className="divider-row">
            <div className="divider-line"></div>
            <div className="divider-badge">
              <span className="divider-text">Log in or Sign-up</span>
            </div>
            <div className="divider-line"></div>
          </div>

          <div className="customer-name">
            <input
              className="customer-name-input"
              placeholder="Enter your full name"
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />
          </div>

          <div className="phone-row">
            <div className="input-group-phone">
              <div className="prefix-box-phone">
                <span className="prefix-text-phone">+91</span>
              </div>
              <input
                className="phone-input"
                placeholder="Enter phone number"
                type="tel"
                value={phone}
                maxLength={10}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, ""); // allow digits only
                  if (value.length <= 10) {
                    setPhone(value);
                  }
                }}
              />
            </div>
            <button
              className="otp-button"
              onClick={handleOtpClick}
              disabled={loader}
            >
              {loader ? (
                <FontAwesomeIcon
                  icon={faSpinner}
                  size="lg"
                  color={Colors.warmWood}
                />
              ) : (
                <span className="otp-text">Get OTP</span>
              )}
            </button>
          </div>
        </div>

        <div
          className="footer"
          style={{
            position: "relative",
            width: "100%",
            textAlign: "center",
          }}
        >
          <img
            src={tasty}
            alt="footer"
            style={{
              width: "100%",
              maxWidth: "420px",
              maxHeight: "25vh", // Responsive max height
              display: "block",
              margin: "0 auto",
              objectFit: "contain",
            }}
          />

          <p
            className="terms-and-conditions-text"
            style={{
              position: "absolute",
              top: "clamp(80px, 15vh, 160px)", // Responsive positioning
              left: "50%",
              transform: "translateX(-50%)",
              width: "90%", // Slightly narrower for better mobile fit
              color: "#6b6b6b",
              fontWeight: "500",
              fontSize: "clamp(9px, 2.5vw, 12px)", // Responsive font size
              lineHeight: "1.2",
              padding: "0 8px", // Add padding for very small screens
            }}
          >
            By continuing, you agree to our{" "}
            <a
              href="/termsconditions"
              className="link-text-terms"
              rel="noopener noreferrer"
              color={Colors.greenCardamom}
              style={{ fontWeight: "bold" }}
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              href="/privacy-policy"
              className="link-text-terms"
              rel="noopener noreferrer"
              color={Colors.greenCardamom}
              style={{ fontWeight: "bold" }}
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
